import './DeckSelector.scss';

import { useDeckContext } from '@context/DeckContext';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { entityKeyAsId } from '@utils/deckHelpers';
import { useNavigate } from 'react-router-dom';
import { DeckSelectorProps } from 'types/DeckSelector.types';

/**
 * Deck Selector component
 * @description - Renders a dropdown list of decks to select from
 * @param props - DeckSelectorProps
 * @constructor
 */
const DeckSelector = (props: DeckSelectorProps) => {
  const { deckNavItems } = props;
  const navigate = useNavigate();
  const { currentDeckId } = useDeckContext();

  /**
   * Navigate to the selected Deck Table of Contents, or return to Deck Index listing
   * @param event - The change event, which will include the unique deck identifier selected
   */
  const handleDeckSelect = (event: SelectChangeEvent) => {
    const selectedDeckId = event.target.value;

    if (selectedDeckId === 'deck-library-home') {
      // Navigate to the deck index page
      navigate('/deck-preparation');
    } else if (selectedDeckId !== currentDeckId) {
      // Only navigate if the User is selecting a different Deck ID than they are currently viewing
      // This will trigger DeckContext to update via route params
      navigate(`/deck-preparation/decks/${selectedDeckId}/toc`);
    }
  };

  return (
    <Box className='deck-selection-wrapper'>
      <Box className='deck-selector'>
        <FormControl className='deck-selector-control'>
          <InputLabel className='deck-selector-label'>Deck</InputLabel>
          <Select
            labelId='deck-selector-label'
            id='deck-select'
            value={currentDeckId || ''}
            onChange={handleDeckSelect}
            label='Deck'
          >
            <MenuItem value='deck-library-home' className='deck-library-home'>
              ← Deck Library
            </MenuItem>
            <Divider />
            {deckNavItems.map((deck, idx) => {
              const deckId = entityKeyAsId(deck.pk);
              const isCurrentDeck = deckId === currentDeckId;

              return (
                <MenuItem
                  key={`deck-${deck.pk}-${idx}`}
                  value={deckId}
                  className={`deck-select-item ${isCurrentDeck ? 'selected' : ''}`}
                >
                  {deck.deck_name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
      <Divider className='nav-divider' />
    </Box>
  );
};

export default DeckSelector;
